import React, { useState, useEffect } from 'react';
import './BaseCharacterSheets.css';
import weaponProficiencies from '../All Cards/WeaponProficiency.js';
import Disorders from '../All Cards/Disorders.js';
import Abilities from '../All Cards/Abilities.js';
import Impairments from '../All Cards/Impairments.js';
import CharacterSheets from './CharacterSheets';
import ArcCharacterSheets from './ArcCharacterSheets.js';


import { usePersistedState } from "./SheetState";


const safeTerm = (v) => (typeof v === 'string' ? v : '');

const BaseCharacterSheets = ({ initialType = 'arc', storageKey } = {}) => {
  const keyBase = storageKey || `kdm_sheet__${initialType}__single`;

  const [name, setName] = usePersistedState(`${keyBase}__name`, '');
  const [gender, setGender] = usePersistedState(`${keyBase}__gender`, { M: false, F: false });
  const [deceased, setDeceased] = usePersistedState(`${keyBase}__deceased`, false);
  const [survival, setSurvival] = usePersistedState(`${keyBase}__survival`, 0);
  const [cannotSpendSurvival, setCannotSpendSurvival] = usePersistedState(`${keyBase}__cannotSpendSurvival`, false);

  const [abilities, setAbilities] = usePersistedState(`${keyBase}__abilities`, {
    dodge: true,
    encourage: false,
    surge: false,
    dash: false,
    fistPump: false,
  });

  const [type, setType] = usePersistedState(`${keyBase}__type`, initialType);
  useEffect(() => {
    setType(initialType);
  }, [initialType, setType]);

  const [stats, setStats] = usePersistedState(`${keyBase}__stats`, {
    movement: { base: 5, gear: 0, token: 0 },
    accuracy: { base: 0, gear: 0, token: 0 },
    strength: { base: 0, gear: 0, token: 0 },
    evasion: { base: 0, gear: 0, token: 0 },
    luck: { base: 0, gear: 0, token: 0 },
    speed: { base: 0, gear: 0, token: 0 }
  });

  const [insanity, setInsanity] = usePersistedState(`${keyBase}__insanity`, 0);
  const [isInsane, setIsInsane] = usePersistedState(`${keyBase}__isInsane`, false);

  const [bodyParts, setBodyParts] = usePersistedState(`${keyBase}__bodyParts`, {
    head: { value: 0, L: false, H: false, impairments: { intracranialHemorrhage: false, deaf: false, blind: [false, false], shatteredJaw: false } },
    arms: { value: 0, L: false, H: false, impairments: { dismemberedArm: [false, false], rupturedMuscle: false, contracture: [false, false, false, false], brokenArm: [false, false] } },
    body: { value: 0, L: false, H: false, impairments: { gapingChestWound: [false, false, false, false, false], destroyedBack: false, brokenRib: [false, false, false, false, false] } },
    waist: { value: 0, L: false, H: false, impairments: { intestinalProlapse: false, warpedPelvis: [false, false, false, false, false], destroyedGenitals: false, brokenHip: false } },
    legs: { value: 0, L: false, H: false, impairments: { dismemberedLeg: [false, false], hamstrung: false, brokenLeg: [false, false] } }
  });

  const [huntXP, setHuntXP] = usePersistedState(`${keyBase}__huntXP`, Array(16).fill(false));
  const handleHuntXPChange = (index) => {
    const updated = [...huntXP];
    updated[index] = !updated[index];
    setHuntXP(updated);
  };

  const [weaponProficiency, setWeaponProficiency] = usePersistedState(`${keyBase}__weaponProficiency`, Array(8).fill(false));
  const [selectedWeapon, setSelectedWeapon] = usePersistedState(`${keyBase}__selectedWeapon`, '');
  const [selectedWeaponData, setSelectedWeaponData] = useState(null);

  useEffect(() => {
    const weaponData = weaponProficiencies.find(wp => wp.name === selectedWeapon);
    setSelectedWeaponData(weaponData || null);
  }, [selectedWeapon]);

  const handleWeaponProficiencyChange = (index) => {
    const updated = [...weaponProficiency];
    updated[index] = !updated[index];
    setWeaponProficiency(updated);
  };

  const handleWeaponChange = (weaponName) => {
    setSelectedWeapon(weaponName);
  };

  const [courageCheckboxes, setCourageCheckboxes] = usePersistedState(`${keyBase}__courageCheckboxes`, Array(9).fill(false));
  const [understandingCheckboxes, setUnderstandingCheckboxes] = usePersistedState(`${keyBase}__understandingCheckboxes`, Array(9).fill(false));
  const [selectedCourage, setSelectedCourage] = usePersistedState(`${keyBase}__selectedCourage`, "");
  const [selectedUnderstanding, setSelectedUnderstanding] = usePersistedState(`${keyBase}__selectedUnderstanding`, "");

  const handleCourageCheckboxChange = (index) => {
    const updated = [...courageCheckboxes];
    updated[index] = !updated[index];
    setCourageCheckboxes(updated);
    if (!updated[2]) setSelectedCourage('');
  };

  const handleUnderstandingCheckboxChange = (index) => {
    const updated = [...understandingCheckboxes];
    updated[index] = !updated[index];
    setUnderstandingCheckboxes(updated);
    if (!updated[2]) setSelectedUnderstanding('');
  };

  const handleCourageRadio = (value) => {
    setSelectedCourage((prev) => (prev === value ? "" : value));
  };

  const handleUnderstandingRadio = (value) => {
    setSelectedUnderstanding((prev) => (prev === value ? "" : value));
  };

  const [selectedDisorders, setSelectedDisorders] = usePersistedState(`${keyBase}__selectedDisorders`, [null, null, null]);
  const handleDisorderChange = (index, value) => {
    const updated = [...selectedDisorders];
    if (value === 'random') {
      updated[index] = Disorders[Math.floor(Math.random() * Disorders.length)] || null;
    } else {
      updated[index] = Disorders.find((disorder) => disorder.term === value) || null;
    }
    setSelectedDisorders(updated);
  };

  const [addedItems, setAddedItems] = usePersistedState(`${keyBase}__addedItems`, []);
  const [selectedType, setSelectedType] = usePersistedState(`${keyBase}__selectedType`, '');
  const [selectedOption, setSelectedOption] = usePersistedState(`${keyBase}__selectedOption`, '');
  const [skipNextHunt, setSkipNextHunt] = usePersistedState(`${keyBase}__skipNextHunt`, false);

  const handleAddItem = (item) => {
    if (!item || typeof item.term !== 'string') return;
    if (!addedItems.some((added) => added.term === item.term)) {
      setAddedItems((prevItems) => [...prevItems, item]);
    }
  };

  const handleRemoveItem = (term) => {
    setAddedItems((prevItems) => prevItems.filter((item) => item?.term !== term));
  };

  const [notes, setNotes] = usePersistedState(`${keyBase}__notes`, "");
  const [oncePerLifetimeRerollUsed, setOncePerLifetimeRerollUsed] = usePersistedState(`${keyBase}__oncePerLifetimeRerollUsed`, false);

  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
    setNotes(textarea.value);
  };

  const toggleGender = (genderKey) => {
    setGender((prevGender) => ({ ...prevGender, [genderKey]: !prevGender[genderKey] }));
  };

  const handleSurvivalChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setSurvival(isNaN(value) ? 0 : value);
  };

  const handleCannotSpendSurvivalChange = () => {
    setCannotSpendSurvival(!cannotSpendSurvival);
  };

  const toggleAbility = (abilityKey) => {
    setAbilities((prev) => ({ ...prev, [abilityKey]: !prev[abilityKey] }));
  };

  const handleStatChange = (statType, valueType, value) => {
    const numericValue = parseInt(value, 10) || 0;
    setStats((prevStats) => ({
      ...prevStats,
      [statType]: { ...prevStats[statType], [valueType]: numericValue },
    }));
  };

  const calculateTotal = (stat) => stat.base + stat.gear + stat.token;

  const handleInsanityChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setInsanity(isNaN(value) ? 0 : value);
  };

  const toggleIsInsane = () => setIsInsane(!isInsane);

  const handleBodyPartValueChange = (part, value) => {
    const numericValue = parseInt(value, 10) || 0;
    setBodyParts((prev) => ({
      ...prev,
      [part]: { ...prev[part], value: numericValue },
    }));
  };

  const toggleL = (part) => {
    setBodyParts((prev) => ({ ...prev, [part]: { ...prev[part], L: !prev[part].L } }));
  };

  const toggleH = (part) => {
    setBodyParts((prev) => ({ ...prev, [part]: { ...prev[part], H: !prev[part].H } }));
  };

  const toggleImpairment = (part, impairmentKey, index = null) => {
    setBodyParts((prev) => {
      const updatedPart = { ...prev[part] };
      const updatedImpairments = { ...updatedPart.impairments };

      if (Array.isArray(updatedImpairments[impairmentKey])) {
        const updatedArray = [...updatedImpairments[impairmentKey]];
        if (index !== null) updatedArray[index] = !updatedArray[index];
        updatedImpairments[impairmentKey] = updatedArray;
      } else {
        updatedImpairments[impairmentKey] = !updatedImpairments[impairmentKey];
      }

      updatedPart.impairments = updatedImpairments;

      return { ...prev, [part]: updatedPart };
    });
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
    setSelectedOption('');
  };

  const handleOptionChange = (event) => setSelectedOption(event.target.value);

  const addItem = () => {
    let selectedData = null;
    if (selectedType === "Abilities") {
      selectedData = Abilities.find((item) => item.term === selectedOption);
    } else if (selectedType === "Impairments") {
      selectedData = Impairments.find((item) => item.term === selectedOption);
    }
    if (selectedData) {
      handleAddItem(selectedData);
      setSelectedOption("");
    }
  };

  const toggleSkipNextHunt = () => setSkipNextHunt(!skipNextHunt);
  const toggleOncePerLifetimeReroll = () => setOncePerLifetimeRerollUsed(!oncePerLifetimeRerollUsed);

  return (
    <div className={type === 'arc' ? 'arc-character-sheet-grid' : 'character-character-sheet-grid'}>

      <div className="left-column">
        <div className="name-wrapper">
          <div
            className="Name"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <label>Name:</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field name-input"
              />

              <div className="gender-wrapper">
                <label>M</label>
                <input type="checkbox" checked={gender.M} onChange={() => toggleGender('M')} />
                <label>F</label>
                <input type="checkbox" checked={gender.F} onChange={() => toggleGender('F')} />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6, paddingRight: 10 }}>
              <input
                type="checkbox"
                checked={deceased}
                onChange={() => setDeceased(!deceased)}
              />
              <label>Deceased</label>
            </div>
          </div>
        </div>

        <div className="survival-wrapper">
          <div className="section survival-container">
            <div className="survival-content">
              <div className="survival-left">
                <div className="survival-section">
                  <label className="survival-label">Survival:</label>
                  <input
                    type="number"
                    value={survival}
                    onChange={handleSurvivalChange}
                    className="input-field"
                  />
                  <label className="survival-checkbox-label">
                    <input
                      type="checkbox"
                      checked={cannotSpendSurvival}
                      onChange={handleCannotSpendSurvivalChange}
                    />
                    Cannot Spend Survival
                  </label>
                </div>

                <div className="insanity-section">
                  <label className="insanity-label">Insanity:</label>
                  <input
                    type="number"
                    value={insanity}
                    onChange={handleInsanityChange}
                    className="input-field"
                  />
                  <label className="insanity-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isInsane}
                      onChange={toggleIsInsane}
                    />
                    Brain Damage
                  </label>
                  <div className="insanity-note">(If your insanity is 3+, you are insane.)</div>
                </div>
              </div>

              <div className="survival-abilities">
                {Object.keys(abilities).map((ability) => (
                  <label key={ability} className="ability-label">
                    <input
                      type="checkbox"
                      checked={abilities[ability]}
                      onChange={() => toggleAbility(ability)}
                      className="ability-checkbox"
                    />
                    <span className="ability-text">
                      {ability === 'fistPump'
                        ? 'Fist Pump'
                        : ability.charAt(0).toUpperCase() + ability.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="stats-wrapper">
          <div className="stats-grid">
            <div className="row-label"></div>
            {["Movement", "Accuracy", "Strength", "Evasion", "Luck", "Speed"].map((stat) => (
              <div key={stat} className="stat-header">{stat}</div>
            ))}

            <div className="row-label">Base</div>
            {["movement", "accuracy", "strength", "evasion", "luck", "speed"].map((stat) => (
              <input
                key={`${stat}-base`}
                type="number"
                value={stats[stat].base}
                onChange={(e) => handleStatChange(stat, 'base', e.target.value)}
                className="stat-input"
              />
            ))}

            <div className="row-label">Gear</div>
            {["movement", "accuracy", "strength", "evasion", "luck", "speed"].map((stat) => (
              <input
                key={`${stat}-gear`}
                type="number"
                value={stats[stat].gear}
                onChange={(e) => handleStatChange(stat, 'gear', e.target.value)}
                className="stat-input"
              />
            ))}

            <div className="row-label">Token</div>
            {["movement", "accuracy", "strength", "evasion", "luck", "speed"].map((stat) => (
              <input
                key={`${stat}-token`}
                type="number"
                value={stats[stat].token}
                onChange={(e) => handleStatChange(stat, 'token', e.target.value)}
                className="stat-input"
              />
            ))}

            <div className="row-label">Total</div>
            {["movement", "accuracy", "strength", "evasion", "luck", "speed"].map((stat) => (
              <div key={`${stat}-total`} className="stat-total">
                {calculateTotal(stats[stat])}
              </div>
            ))}
          </div>
        </div>

        <div className="body-part-wrapper">
          {Object.keys(bodyParts).map((part) => (
            <div className="body-part" key={part}>
              <div className="body-part-container">
                <div className="body-part-info">
                  <label className="body-part-label">
                    {part.charAt(0).toUpperCase() + part.slice(1)}:
                  </label>
                  <input
                    type="number"
                    value={bodyParts[part].value}
                    onChange={(e) => handleBodyPartValueChange(part, e.target.value)}
                    className="input-field"
                  />
                  <label>
                    <input type="checkbox" checked={bodyParts[part].L} onChange={() => toggleL(part)} />
                    L
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={bodyParts[part].H}
                      onChange={() => toggleH(part)}
                      className="bold-h-checkbox"
                    />
                    H
                  </label>
                </div>

                <div className="impairments-container">
                  {Object.keys(bodyParts[part].impairments).map((impairment) => {
                    const formattedImpairment = impairment
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())
                      .trim();

                    return (
                      <div key={impairment} className="impairment-item">
                        <label className="impairment-label">
                          {Array.isArray(bodyParts[part].impairments[impairment]) ? (
                            <>
                              {bodyParts[part].impairments[impairment].map((checked, index) => (
                                <input
                                  key={index}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    toggleImpairment(part, impairment, index);

                                    if (!checked) {
                                      const impairmentDetails = Impairments.find((imp) => imp.term === formattedImpairment);
                                      if (impairmentDetails) handleAddItem(impairmentDetails);
                                      else handleAddItem({ term: formattedImpairment, definition: "No description available" });
                                    } else {
                                      handleRemoveItem(formattedImpairment);
                                    }
                                  }}
                                  className="impairment-checkbox"
                                />
                              ))}
                              <span>{formattedImpairment}</span>
                            </>
                          ) : (
                            <>
                              <input
                                type="checkbox"
                                checked={bodyParts[part].impairments[impairment]}
                                onChange={() => {
                                  toggleImpairment(part, impairment);

                                  const isChecked = !bodyParts[part].impairments[impairment];
                                  if (isChecked) {
                                    const impairmentDetails = Impairments.find((imp) => imp.term === formattedImpairment);
                                    if (impairmentDetails) handleAddItem(impairmentDetails);
                                    else handleAddItem({ term: formattedImpairment, definition: "No description available" });
                                  } else {
                                    handleRemoveItem(formattedImpairment);
                                  }
                                }}
                                className="impairment-checkbox"
                              />
                              <span>{formattedImpairment}</span>
                            </>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="middle-column">

        <div className="render-hunt-xp-section">
          {type === 'arc' ? (
            <ArcCharacterSheets
              showHuntXP={true}
              storageKey={`${keyBase}__arc`}
            />
          ) : type === 'character' ? (
            <CharacterSheets
              showHuntXP={true}
              storageKey={`${keyBase}__character`}
            />
          ) : (
            <p>No Hunt XP Character Sheets Available</p>
          )}
        </div>

        <div className="weapon-proficiency">
          <div className="proficiency-row">
            <label className="proficiency-label">Weapon Proficiency:</label>
            <div className="proficiency-checkboxes">
              {Array.from({ length: 8 }, (_, index) => (
                <input
                  key={index}
                  type="checkbox"
                  checked={weaponProficiency[index]}
                  onChange={() => handleWeaponProficiencyChange(index)}
                  className={`hunt-xp-checkbox ${[2, 7].includes(index) ? 'bold' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="proficiency-details">
            <label>Type:</label>
            <select value={selectedWeapon} onChange={(e) => handleWeaponChange(e.target.value)}>
              <option value="">Select Weapon</option>
              {weaponProficiencies.map((wp, index) => (
                <option key={index} value={wp.name}>
                  {wp.name}
                </option>
              ))}
            </select>

            <input type="checkbox" checked disabled className="hunt-xp-checkbox bold" />
            <span>📖 Specialist</span>

            <input type="checkbox" checked disabled className="hunt-xp-checkbox bold" />
            <input type="checkbox" checked disabled className="hunt-xp-checkbox bold" />
            <span>📖 Master</span>
          </div>

          {selectedWeaponData && (weaponProficiency[2] || weaponProficiency[7]) && (
            <div className="weapon-info">
              {weaponProficiency[2] && (
                <p><strong>Specialization:</strong> {selectedWeaponData.specialization}</p>
              )}
              {weaponProficiency[7] && (
                <p><strong>Mastery:</strong> {selectedWeaponData.mastery}</p>
              )}
            </div>
          )}
        </div>

        <div className="courageUnderstanding">
          <div className="courage-section">
            <h3>Courage</h3>
            <div className="courage-checkboxes">
              {Array.from({ length: 9 }, (_, index) => (
                <input
                  key={index}
                  type="checkbox"
                  className={`courage-checkbox ${[2, 8].includes(index) ? 'bold' : ''}`}
                  checked={courageCheckboxes[index]}
                  onChange={() => handleCourageCheckboxChange(index)}
                />
              ))}
            </div>

            <div className="milestones">
              <label>
                <input type="checkbox" checked disabled className="milestone-checkbox bold" />
                📖 Bold
              </label>
              <label>
                <input type="checkbox" checked disabled className="milestone-checkbox filled" />
                <input type="checkbox" checked disabled className="milestone-checkbox filled" />
                📖 See the Truth
              </label>
            </div>

            {courageCheckboxes[2] && (
              <div className="radio-options">
                {(selectedCourage === '' || selectedCourage === 'stalwart') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__courage`}
                      checked={selectedCourage === 'stalwart'}
                      onClick={() => handleCourageRadio('stalwart')}
                      readOnly
                    />
                    Stalwart: Can't be knocked down by brain trauma or intimidate.
                  </label>
                )}
                {(selectedCourage === '' || selectedCourage === 'prepared') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__courage`}
                      checked={selectedCourage === 'prepared'}
                      onClick={() => handleCourageRadio('prepared')}
                      readOnly
                    />
                    Prepared: Add Hunt XP to your roll when determining a straggler.
                  </label>
                )}
                {(selectedCourage === '' || selectedCourage === 'matchmaker') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__courage`}
                      checked={selectedCourage === 'matchmaker'}
                      onClick={() => handleCourageRadio('matchmaker')}
                      readOnly
                    />
                    Matchmaker: Spend 1 endeavor to trigger Intimacy story event.
                  </label>
                )}
              </div>
            )}
          </div>

          <div className="divider"></div>

          <div className="understanding-section">
            <h3>Understanding</h3>
            <div className="understanding-checkboxes">
              {Array.from({ length: 9 }, (_, index) => (
                <input
                  key={index}
                  type="checkbox"
                  className={`understanding-checkbox ${[2, 8].includes(index) ? 'bold' : ''}`}
                  checked={understandingCheckboxes[index]}
                  onChange={() => handleUnderstandingCheckboxChange(index)}
                />
              ))}
            </div>

            <div className="milestones">
              <label>
                <input type="checkbox" checked disabled className="milestone-checkbox bold" />
                📖 Insight
              </label>
              <label>
                <input type="checkbox" checked disabled className="milestone-checkbox filled" />
                <input type="checkbox" checked disabled className="milestone-checkbox filled" />
                📖 White Secret
              </label>
            </div>

            {understandingCheckboxes[2] && (
              <div className="radio-options">
                {(selectedUnderstanding === '' || selectedUnderstanding === 'analyze') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__understanding`}
                      checked={selectedUnderstanding === 'analyze'}
                      onClick={() => handleUnderstandingRadio('analyze')}
                      readOnly
                    />
                    Analyze: Look at the top AI card and return it to the top of the deck.
                  </label>
                )}
                {(selectedUnderstanding === '' || selectedUnderstanding === 'explore') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__understanding`}
                      checked={selectedUnderstanding === 'explore'}
                      onClick={() => handleUnderstandingRadio('explore')}
                      readOnly
                    />
                    Explore: Add +2 to your Investigate roll results.
                  </label>
                )}
                {(selectedUnderstanding === '' || selectedUnderstanding === 'tinker') && (
                  <label>
                    <input
                      type="radio"
                      name={`${keyBase}__understanding`}
                      checked={selectedUnderstanding === 'tinker'}
                      onClick={() => handleUnderstandingRadio('tinker')}
                      readOnly
                    />
                    Tinker: +1 endeavor when a returning survivor.
                  </label>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="render-fighting-arts-section">
          {type === 'arc' ? (
            <ArcCharacterSheets showFightingArts={true} storageKey={`${keyBase}__arc`} />
          ) : type === 'character' ? (
            <CharacterSheets showFightingArts={true} storageKey={`${keyBase}__character`} />
          ) : (
            <p>No Character Sheets Available</p>
          )}
        </div>

        <div className="disorders">
          <label>
            Disorders: <span className="disorder-max-text">(Maximum 3)</span>
          </label>

          {[0, 1, 2].map((index) => (
            <div key={index} className="disorder-dropdown-section">
              <select
                value={selectedDisorders[index]?.term || ''}
                onChange={(e) => handleDisorderChange(index, e.target.value)}
              >
                <option value="">Select Disorder</option>
                <option value="random">🎲 Random Selection</option>
                {Disorders.slice()
                  .sort((a, b) => safeTerm(a?.term).localeCompare(safeTerm(b?.term)))
                  .filter(
                    (disorder) =>
                      !selectedDisorders.some(
                        (selected, idx) => selected?.term === disorder.term && idx !== index
                      )
                  )
                  .map((disorder) => (
                    <option key={disorder.term} value={disorder.term}>
                      {disorder.term}
                    </option>
                  ))}
              </select>

              {selectedDisorders[index] && (
                <div className="disorder-details">
                  <strong>{selectedDisorders[index].term}</strong>
                  <p><em>{selectedDisorders[index]['flavor text']}</em></p>
                  <p>{selectedDisorders[index].definition}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="AbilitiesImpairments">
          <div className="header">
            <label>Abilities & Impairments:</label>
            <label className="skip-hunt">
              <input type="checkbox" checked={skipNextHunt} onChange={toggleSkipNextHunt} />
              🔒 <span className="small-text">Skip Next Hunt</span>
            </label>
          </div>

          <div className="abilities-dropdowns">
            <select value={selectedType} onChange={handleTypeChange}>
              <option value="">Select Type</option>
              <option value="Abilities">Abilities</option>
              <option value="Impairments">Impairments</option>
            </select>

            {selectedType && (
              <select value={selectedOption} onChange={handleOptionChange}>
                <option value="">Select {selectedType}</option>
                {(selectedType === "Abilities" ? Abilities : Impairments)
                  .slice()
                  .sort((a, b) => safeTerm(a?.term).localeCompare(safeTerm(b?.term)))
                  .filter((item) => !addedItems.some((added) => added.term === item.term))
                  .map((item) => (
                    <option key={item.term} value={item.term}>
                      {item.term}
                    </option>
                  ))}
              </select>
            )}

            <button onClick={addItem} disabled={!selectedOption}>Add</button>
          </div>

          <div className="item-list">
            {addedItems.some((item) => Abilities.some((ability) => ability.term === item.term)) && (
              <strong>Abilities:</strong>
            )}

            {addedItems
              .filter((item) => Abilities.some((ability) => ability.term === item.term))
              .slice()
              .sort((a, b) => safeTerm(a?.term).localeCompare(safeTerm(b?.term)))
              .map((item) => (
                <div key={item.term} className="item-card">
                  <button className="remove-btn" onClick={() => handleRemoveItem(item.term)}>❌</button>
                  <strong>{item.term}</strong>
                  <p>{item.definition}</p>
                </div>
              ))}

            {addedItems.some((item) => Impairments.some((imp) => imp.term === item.term)) && (
              <strong>Impairments:</strong>
            )}

            {addedItems
              .filter((item) => Impairments.some((imp) => imp.term === item.term))
              .slice()
              .sort((a, b) => safeTerm(a?.term).localeCompare(safeTerm(b?.term)))
              .map((item) => (
                <div key={item.term} className="item-card">
                  <button className="remove-btn" onClick={() => handleRemoveItem(item.term)}>❌</button>
                  <strong>{item.term}</strong>
                  <p>{item.definition}</p>
                </div>
              ))}
          </div>
        </div>

        <div className="notes-wrapper">
          <div className="notes-header">
            <label>Notes:</label>
            <label className="small-text">
              <input type="checkbox" checked={oncePerLifetimeRerollUsed} onChange={toggleOncePerLifetimeReroll} />
              Once Per Lifetime Reroll Used
            </label>
          </div>
          <textarea
            value={notes}
            onChange={handleInput}
            placeholder="Type your notes here..."
            className="notes-textarea"
            rows={1}
          />
        </div>
      </div>

      {type === 'arc' && (
        <div className="right-column">
          <div className="render-philosophy-section">
            <ArcCharacterSheets showPhilosophy={true} storageKey={`${keyBase}__arc`} />
          </div>

          <div className="render-philosophy-section">
            <ArcCharacterSheets showKnowledge={true} storageKey={`${keyBase}__arc`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseCharacterSheets;
